// CreditPackEditor — Owner-only Tabelle der verkaufbaren Credit-Packs
// (eve_credit_packs). Pro Zeile editierbar: Label, Credits, Preis (EUR-Eingabe →
// price_cents), stripe_price_id, Aktiv-Toggle. Speichern → PUT /admin/credit-packs/:id.
// Self-contained: lädt selbst via useAuth-Token, hält eigenen Draft-State.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import {
  listCreditPacks,
  updateCreditPack,
  type CreditPackRow,
} from '@/lib/admin-credits-api';

interface PackDraft {
  label: string;
  credits: string;
  priceEuro: string;
  stripePriceId: string;
  active: boolean;
}

function toDraft(row: CreditPackRow): PackDraft {
  return {
    label: row.label,
    credits: String(row.credits),
    priceEuro: (row.price_cents / 100).toFixed(2),
    stripePriceId: row.stripe_price_id ?? '',
    active: row.active === 1,
  };
}

function draftEqualsRow(draft: PackDraft, row: CreditPackRow): boolean {
  return (
    draft.label === row.label &&
    draft.credits === String(row.credits) &&
    draft.priceEuro === (row.price_cents / 100).toFixed(2) &&
    draft.stripePriceId === (row.stripe_price_id ?? '') &&
    draft.active === (row.active === 1)
  );
}

export function CreditPackEditor() {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<CreditPackRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, PackDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await listCreditPacks(accessToken);
      setRows(data);
      setDrafts(Object.fromEntries(data.map((r) => [r.id, toDraft(r)])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function patchDraft(id: string, patch: Partial<PackDraft>) {
    setDrafts((prev) => {
      const current = prev[id];
      if (!current) return prev;
      return { ...prev, [id]: { ...current, ...patch } };
    });
  }

  async function save(row: CreditPackRow) {
    if (!accessToken) return;
    const draft = drafts[row.id];
    if (!draft) return;
    const credits = Number.parseInt(draft.credits, 10);
    const priceEuro = Number.parseFloat(draft.priceEuro.replace(',', '.'));
    if (!draft.label.trim()) {
      setError(`${row.id}: Label ist Pflicht.`);
      return;
    }
    if (!Number.isFinite(credits) || credits <= 0) {
      setError(`${draft.label}: Credits müssen > 0 sein.`);
      return;
    }
    if (!Number.isFinite(priceEuro) || priceEuro < 0) {
      setError(`${draft.label}: Preis muss ≥ 0 sein.`);
      return;
    }
    setSavingId(row.id);
    setError(null);
    try {
      await updateCreditPack(accessToken, row.id, {
        label: draft.label.trim(),
        credits,
        price_cents: Math.round(priceEuro * 100),
        stripe_price_id: draft.stripePriceId.trim() || null,
        active: draft.active,
        sort_order: row.sort_order,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <section className="glass-strong rounded-2xl p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold">Credit-Pakete</h2>
        <p className="text-[12px] text-neutral-500">
          Verkaufbare Stripe-Pakete. Preis wird in EUR eingegeben, intern als Cent gespeichert.
        </p>
      </div>

      {error ? (
        <div className="mb-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade Credit-Pakete…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">
          Keine Credit-Pakete hinterlegt.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const draft = drafts[row.id];
            if (!draft) return null;
            const dirty = !draftEqualsRow(draft, row);
            return (
              <div
                key={row.id}
                className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                    {row.id}
                  </span>
                  <label className="flex items-center gap-2 text-[11px] text-neutral-400">
                    <input
                      type="checkbox"
                      checked={draft.active}
                      onChange={(e) => patchDraft(row.id, { active: e.target.checked })}
                      className="size-3.5 accent-amber-500"
                    />
                    Aktiv
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <PackField label="Label">
                    <input
                      type="text"
                      value={draft.label}
                      onChange={(e) => patchDraft(row.id, { label: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-amber-500/40"
                      aria-label={`Label für ${row.id}`}
                    />
                  </PackField>
                  <PackField label="Credits">
                    <input
                      type="number"
                      value={draft.credits}
                      min={1}
                      step={1}
                      onChange={(e) => patchDraft(row.id, { credits: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-[12px] tabular outline-none focus:border-amber-500/40"
                      aria-label={`Credits für ${row.id}`}
                    />
                  </PackField>
                  <PackField label="Preis (EUR)">
                    <input
                      type="number"
                      value={draft.priceEuro}
                      min={0}
                      step="0.01"
                      onChange={(e) => patchDraft(row.id, { priceEuro: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-[12px] tabular outline-none focus:border-amber-500/40"
                      aria-label={`Preis für ${row.id}`}
                    />
                  </PackField>
                  <PackField label="Stripe Price-ID">
                    <input
                      type="text"
                      value={draft.stripePriceId}
                      placeholder="price_…"
                      onChange={(e) => patchDraft(row.id, { stripePriceId: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] outline-none focus:border-amber-500/40"
                      aria-label={`Stripe-Price-ID für ${row.id}`}
                    />
                  </PackField>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => void save(row)}
                    disabled={!dirty || savingId === row.id}
                    className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
                  >
                    {savingId === row.id ? 'Speichere…' : 'Speichern'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PackField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      {children}
    </div>
  );
}
